/**
 * Global module holds all global functions
 * @class Global
 * @since 0.0.1
 */
import $ from 'jquery'
import environment from './environment'
import zxcvbn from 'zxcvbn'
import store from './store'

/**
 * Initialize csrfToken for use in backend calls.
 * @var {string}
 * @memberof Global
 * @version 0.0.1
 */
let csrfToken

/**
 * Required for some IE specific logic however the only IE we will test for is 10.
 * @var {boolean}
 * @memberof Global
 * @version 0.0.4
 */
let isIE10 = false

/**
 * Create a variable that will hold a list of functions to run on window resize.
 * @var {array}
 * @memberof Global
 * @version 0.0.4
 */
let resizeHandlers = []

/**
 * To check if the session has been set in locolStorage and if it is expired.
 * @function
 * @param {string} size - A string representing the breakpoint you want returned
 * @returns {integer} - The value of the specified breakpoint or 0 if no match is found
 * @memberof Global
 * @version 0.0.4
 */
let getResponsiveBreakpoint = size => {
	const sizes = {
		'xs': 480,
		'sm': 768,
		'md': 992,
		'lg': 1200
	}

	return sizes[size] ? sizes[size] : 0
}

/**
 * Create a variable that will hold the medium screen breakpoint.
 * @var {integer}
 * @memberof Global
 * @version 0.0.4
 */
let resBreakpointMd = getResponsiveBreakpoint('md')

/**
 * To initialize the html by adding any necessary classes (currently only being used for IE10).
 * @function
 * @returns {undefined}
 * @memberof Global
 * @version 0.0.4
 */
let handleInit = () => {
	isIE10 = !!navigator.userAgent.match(/MSIE 10.0/)

	if (isIE10) {
		$('html').addClass('ie10').addClass('ie')
	}
}

/**
 * To run each of the functions that needs to be called on window resize.
 * @function
 * @returns {undefined}
 * @memberof Global
 * @version 0.0.4
 */
let runResizeHandlers = () => {
	for (let i = 0; i < resizeHandlers.length; i++) {
		let each = resizeHandlers[i]
		each.call()
	}
}

/**
 * To determine if the resive is legit or forced and then runs the necessary resize handlers
 * @function
 * @returns {undefined}
 * @memberof Global
 * @version 0.0.4
 */
let handleOnResize = () => {
	let windowWidth = $(window).width()
	let windowHeight = $(window).height()
	let resize

	$(window).resize(() => {
		if ($(window).width() !== windowWidth || $(window).height() !== windowHeight) {
			windowWidth = $(window).width()
			windowHeight = $(window).height()
			if (resize) {
				clearTimeout(resize)
			}
			resize = setTimeout(() => {
				runResizeHandlers()
			}, 50)
		}
	})
}

/**
 * To append to the list of resize handlers to run on window resize
 * @function
 * @param {string} func - The name of the function that needs to be run
 * @returns {undefined}
 * @memberof Global
 * @version 0.0.4
 */
let addResizeHandler = func => {
	resizeHandlers.push(func)
}

/**
 * To initialize material design elements
 * @function
 * @returns {undefined}
 * @memberof Global
 * @version 0.0.4
 */
let handleMaterialDesign = () => {
	// Material design ckeckbox and radio effects
	$('body').on('click', '.md-checkbox > label, .md-radio > label', () => {
		let the = $(this)
		// find the first span which is our circle/bubble
		let el = $(this).children('span:first-child')

		// add the bubble class (we do this so it doesnt show on page load)
		el.addClass('inc')

		// clone it
		let newone = el.clone(true)

		// add the cloned version before our original
		el.before(newone)

		// remove the original so that it is ready to run on next click
		$('.' + el.attr('class') + ':last', the).remove()
	})

	if ($('body').hasClass('page-md')) {
		// Material design click effect
		let element
		let circle
		let d
		let x
		let y

		$('body').on('click', 'a.btn, button.btn, input.btn, label.btn', e => {
			element = $(e.target)

			if (element.find('.md-click-circle').length === 0) {
				element.prepend('<span class="md-click-circle"></span>')
			}

			circle = element.find('.md-click-circle')
			circle.removeClass('md-click-animate')

			if (!circle.height() && !circle.width()) {
				d = Math.max(element.outerWidth(), element.outerHeight())
				circle.css({height: d, width: d})
			}

			x = e.pageX - element.offset().left - circle.width() / 2
			y = e.pageY - element.offset().top - circle.height() / 2

			circle.css({top: y + 'px', left: x + 'px'}).addClass('md-click-animate')

			setTimeout(() => {
				circle.remove()
			}, 1000)
		})
	}

	// Floating labels
	let handleInput = el => {
		if (el.val() !== '') {
			el.addClass('edited')
		} else {
			el.removeClass('edited')
		}
	}

	$('body').on('keydown', '.form-md-floating-label .form-control', e => {
		handleInput($(this))
	})
	$('body').on('blur', '.form-md-floating-label .form-control', e => {
		handleInput($(this))
	})

	$('.form-md-floating-label .form-control').each(() => {
		if ($(this).val().length > 0) {
			$(this).addClass('edited')
		}
	})
}

/**
 * To get the width and height of the viewport
 * @function
 * @returns {object} - The width and height of the viewport
 * @memberof Global
 * @version 0.0.4
 */
let getViewPort = () => {
	let e = window
	let	a = 'inner'
	if (!('innerWidth' in window)) {
		a = 'client'
		e = document.documentElement || document.body
	}

	return {
		width: e[a + 'Width'],
		height: e[a + 'Height']
	}
}

let handleHeight = () => {
	// Do nothing at this time
}

/**
 * To set the content section to be the full height based on the size of the browser window
 * @function
 * @returns {undefined}
 * @memberof Global
 * @version 0.0.4
 */
let handle100HeightContent = () => {
	$('.full-height-content').each(() => {
		let target = $(this)
		let height

		height = getViewPort().height - $('.page-header').outerHeight(true) - $('.page-footer').outerHeight(true) - $('.page-title').outerHeight(true) - $('.page-bar').outerHeight(true)

		// NOTE: have not added all logic from Metronic yet
		target.css('min-height', height)
	})
}

/**
 * To set the sidebar and content to be the full height based on the size of the browser window
 * @function
 * @returns {undefined}
 * @memberof Global
 * @version 0.0.4
 */
let handleSidebarAndContentHeight = () => {
	// NOTE: this will deliver a strange height until I handle the sidebar
	let content = $('.page-content')
	let sidebar = $('.page-sidebar')
	let height

	let headerHeight = $('.page-header').outerHeight()
	let footerHeight = $('.page-footer').outerHeight()

	if (!headerHeight) {
		headerHeight = 0
	}
	if (!footerHeight) {
		footerHeight = 0
	}

	if (getViewPort().width < resBreakpointMd) {
		height = getViewPort().height - headerHeight - footerHeight
	} else {
		height = sidebar.height() + 20
	}

	if ((height + headerHeight + footerHeight) <= getViewPort().height) {
		height = getViewPort().height - headerHeight - footerHeight
	}

	content.css('min-height', height)
}

/**
 * To calculate the height or the fixed sidebar
 * @function
 * @returns {integer} - The height of the sidebar
 * @memberof Global
 * @version 0.0.4
 */
let calculateFixedSidebarViewportHeight = () => {
	const sidebarHeight = getViewPort().height - $('.page-header').outerHeight(true)

	return sidebarHeight
}

/**
 * To initialize the sidebar
 * @function
 * @returns {undefined}
 * @memberof Global
 * @version 0.0.4
 */
let handleFixedSidebar = () => {
	let menu = $('.page-sidebar-menu')
	handleSidebarAndContentHeight()

	if (getViewPort().width >= resBreakpointMd && !$('body').hasClass('page-sidebar-menu-not-fixed')) {
		menu.attr('data-height', calculateFixedSidebarViewportHeight())
		// NOTE: need to add slimscroll first
		// destroySlimScroll(menu);
		// initSlimScroll(menu);
		handleSidebarAndContentHeight()
	}
}

/**
 * To handle the sidebar menu toggler on responsive
 * @function
 * @returns {undefined}
 * @memberof Global
 * @version 0.0.4
 */
let handleResponsiveSidebarToggler = () => {
	$('a.responsive-toggler').on('click', e => {
		let sidebar = $('.page-sidebar')

		if (sidebar.hasClass('in')) {
			sidebar.removeClass('in')
		} else {
			sidebar.addClass('in')
		}

		$(window).trigger('resize')
	})
}

/**
 * To handle the sidebar menu toggler
 * @function
 * @returns {undefined}
 * @memberof Global
 * @version 0.0.4
 */
let handleSidebarToggler = () => {
	// handle sidebar show/hide
	$('body').on('click', '.sidebar-toggler', e => {
		let body = $('body')
		let sidebar = $('.page-sidebar')
		let sidebarMenu = $('.page-sidebar-menu')
		$('.sidebar-search', sidebar).removeClass('open')

		if (body.hasClass('page-sidebar-closed')) {
			body.removeClass('page-sidebar-closed')
			sidebarMenu.removeClass('page-sidebar-menu-closed')
		} else {
			body.addClass('page-sidebar-closed')
			sidebarMenu.addClass('page-sidebar-menu-closed')
			if (body.hasClass('page-sidebar-fixed')) {
				sidebarMenu.trigger('mouseleave')
			}
		}

		$(window).trigger('resize')
	})
}

// Handles Bootstrap Tabs.
let handleTabs = () => {
	// fix content height on tab click
	$('body').on('shown.bs.tab', 'a[data-toggle="tab"]', () => {
		handleSidebarAndContentHeight()
	})
}

// Handle sidebar menu
let handleSidebarMenu = function () {
	setTimeout(function () {
		$('.page-sidebar-menu').on('click', 'li > a.nav-link', function (e) {
			if ($(this).hasClass('nav-toggle')) {
				return
			}
			if (getViewPort().width < resBreakpointMd && $('.page-sidebar').hasClass('in')) { // close the menu on mobile view while loading a page
				$('.page-header .responsive-toggler').click()
			}
		})
	}, 1000)
}

export default {
	/**
	 * A function to return the numerical value of a boolean.
	 * @function
	 * @param {boolean} value - The boolean value we want to convert.
	 * @returns {integer} - Either 1 or 0 based on the boolean passed in.
	 * @memberof Global
	 * @version 0.0.6
	 */
	convertBooleanToInt (value) {
		return value ? 1 : 0
	},
	/**
	 * Ajax wrapper for making backend calls.
	 * @function
	 * @param {object} options - Options for the ajax call being made.
	 * @param {boolean} noRetry - Used to prevent retrying the call.
	 * @returns {undefined}
	 * @memberof Global
	 * @version 0.0.5
	 */
	$ajax (options, noRetry) {
		options.beforeSend = xhr => {
			csrfToken = ('' + Math.random()).replace('0.', '')
			if (document.cookie !== '' && document.cookie.indexOf('csrf_token=') !== -1) {
				csrfToken = document.cookie.split('csrf_token=')[1]
			} else {
				if (process.env.NODE_ENV === 'development') {
					document.cookie = 'csrf_token=' + csrfToken
				} else {
					document.cookie = 'csrf_token=' + csrfToken + ';domain=.unoapp.io'
				}
			}
			xhr.setRequestHeader('X-CSRF-Token', csrfToken)
		}

		// Allow cross-domain cookies
		options.xhrFields = {
			withCredentials: true
		}

		let localhost = 'https://api.staging.unoapp.io'
		if (process.env.NODE_ENV === 'development') {
			if (environment.fake_backend) {
				// localhost = 'http://localhost:8008'
			} else {
				// localhost = 'http://localhost:4200'
			}
		}

		if (options.url.indexOf(localhost) === -1) {
			options.url = localhost + options.url
		}

		$.ajax(options)
	},
	/**
	 * Wrapper for handling whether or not the auth token needs refreshing.
	 * @function
	 * @param {function} callback - A callback function to be used for success and error response handling.
	 * @returns {function} - The refreshWrapper function.
	 * @memberof Global
	 * @version 0.0.9
	 */
	refreshWrapper (callback) {
		return e => {
			if (e.status === 401 && e.responseJSON && e.responseJSON.declaration === 'auth_token_needs_refresh') {
				// Refresh token
				$.ajax({
					url: 'https://api.staging.unoapp.io/auth/refresh',
					method: 'POST',
					dataType: 'json',
					xhrFields: {
						withCredentials: true
					},
					beforeSend (xhr) {
						xhr.setRequestHeader('X-CSRF-Token', csrfToken)
					},
					success (response) {
						store.dispatch('setV3Token', response.payload.token)

						callback(response, 'success')
					},
					error (refreshError) {
						callback(refreshError, 'error')
					}
				})
			} else {
				let status = (e.status === 0 || e.status === 401 || e.status === 403 || e.status === 404 || e.status === 500 || (e.responseJSON && e.responseJSON.status === 'error')) ? 'error' : 'success'
				callback(e, status)
			}
		}
	},
	/**
	 * Call to pull in all Google Fonts from the Google api.
	 * @function
	 * @returns {undefined}
	 * @memberof Global
	 * @version 0.0.9
	 */
	getGoogleFonts () {
		return new Promise((resolve, reject) => {
			$.ajax({
				method: 'GET',
				dataType: 'json',
				url: 'https://www.googleapis.com/webfonts/v1/webfonts?key=AIzaSyAGnCiR_SmDQ7ZQW5qhWx7Rdgntn91xkoE',
				success (response) {
					resolve(response)
				},
				error (e) {
					reject(e)
				}
			})
		})
	},
	/**
	 * Global function for disabling buttons when a request is made.
	 * @function
	 * @param {object} event - The click event that triggered this function.
	 * @returns {object} An object of functions for manipulating the selected button
	 * @class
	 * @memberof Global
	 * @extends Global
	 * @version 0.0.5
	 * @example Set up a variable `my_variable` that calls this function
	 * When the backend call is a success call my_variable.complete()
	 * When the backend call is an error call my_variable.cancel()
	 */
	disableButton (event) {
		let button
		if (event.type === 'submit') {
			button = $(event.target).find('button[type=submit]')
		} else {
			button = $(event.target)
		}

		let originalText
		let originalHtml

		// For bypassing the DOM when unit testing
		if (!button[0]) {
			originalText = 'This is for save testing.'
		} else {
			originalHtml = button.html()
			originalText = button.text()
		}
		let originalColor = button.css('background-color')
		let whileDisabled, complete

		button.addClass('background-animate')

		if (originalText.toLowerCase().indexOf('save') !== -1) {
			whileDisabled = 'saving'
			complete = 'complete'
		}

		if (originalText.toLowerCase().indexOf('submit') !== -1) {
			whileDisabled = 'sending'
			complete = 'complete'
		}

		if (whileDisabled) {
			button.html(whileDisabled)
		}

		button.prop('disabled', true)
		return {
			/**
			 * To complete the button animation and restore the button.
			 * @function
			 * @returns {undefined}
			 * @memberof Global.disableButton
			 * @version 0.0.7
			 */
			complete () {
				return new Promise((resolve, reject) => {
					setTimeout(() => {
						if (complete) {
							button.html(complete)
							button.css({'background-color': '#5cb85c'})

							setTimeout(() => {
								button.css({'background-color': originalColor})
								button.html(originalHtml)
								resolve()
							}, 1000)
						} else {
							button.prop('disabled', false)
							resolve()
						}
					}, 100)
				})
			},
			/**
			 * To cancel the button animation and restore the button.
			 * @function
			 * @returns {undefined}
			 * @memberof Global.disableButton
			 * @version 0.0.7
			 */
			cancel () {
				setTimeout(() => {
					if (whileDisabled || complete) {
						button.html(originalHtml)
					}
					button.prop('disabled', false)
				}, 100)
			}
		}
	},
	/**
	 * Call to zxcvbn API to check password strength.
	 * @function
	 * @param {string} password - The password to be tested.
	 * @returns {string} The password strength.
	 * @memberof Global
	 * @version 0.0.4
	 */
	passwordStrengthCheck (password) {
		// We will assume that any password longer than 50 characters is strong enough
		const result = zxcvbn(password.substring(0, 50), ['unoapp', 'street'])

		const crackTime = result.crack_times_seconds.offline_slow_hashing_1e4_per_second

		if (password.length < 8) {
			return 'Password must be 8 characters minimum'
		}

		if (crackTime < 60 * 60 * 24) {
			return 'Very Weak'
		} else if (crackTime < 60 * 60 * 24 * 365) {
			return 'Weak'
		} else if (crackTime < 60 * 60 * 24 * 365 * 100) {
			return 'OK'
		} else {
			return 'Strong'
		}
	},
	/**
	 * List of functions to run to initialize the Metronic portion of the app
	 * @function
	 * @returns {undefined}
	 * @memberof Global
	 * @version 0.0.4
	 */
	init () {
		// IMPORTANT!!!: Do not modify the core handlers call order.

		// Core handlers
		handleInit() // initialize core variables
		handleOnResize() // set and handle responsive

		// UI Component handlers
		handleMaterialDesign() // handle material design
		// handleiCheck() // handles custom icheck radio and checkboxes
		// handleBootstrapSwitch() // handle bootstrap switch plugin
		// handleScrollers() // handles slim scrolling contents
		// handleFancybox() // handle fancy box
		// handleSelect2() // handle custom Select2 dropdowns
		// handlePortletTools() // handles portlet action bar functionality(refresh, configure, toggle, remove)
		// handleAlerts() //handle closabled alerts
		// handleDropdowns() // handle dropdowns
		// handleTabs() // handle tabs
		// handleTooltips() // handle bootstrap tooltips
		// handlePopovers() // handles bootstrap popovers
		// handleAccordions() //handles accordions
		// handleModals() // handle modals
		// handleBootstrapConfirmation() // handle bootstrap confirmations
		// handleTextareaAutosize() // handle autosize textareas
		// handleCounterup() // handle counterup instances

		// Handle group element heights
		addResizeHandler(handleHeight) // handle auto calculating height on window resize
	},
	/**
	 * List of functions to run to initialize the app structure
	 * @function
	 * @returns {undefined}
	 * @memberof Global
	 * @version 0.0.4
	 */
	initApp () {
		this.initHeader()
		this.initSidebar(null)
		this.initContent()
	},
	initHeader () {
		// This function currently doesn't require any content (will be needed when we start using the location sidebar)
	},
	/**
	 * List of functions to run to initialize the side navigation bar
	 * @function
	 * @returns {undefined}
	 * @memberof Global
	 * @version 0.0.4
	 */
	initSidebar () {
		handleFixedSidebar() // handles fixed sidebar menu
		handleSidebarMenu() // handles main menu
		handleSidebarToggler() // handles sidebar hide/show
		handleResponsiveSidebarToggler() // handles sidebar hide/show on responsive

		// addResizeHandler(handleFixedSidebar); // reinitialize fixed sidebar on window resize
	},
	/**
	 * List of functions to run to initialize the app content
	 * @function
	 * @returns {undefined}
	 * @memberof Global
	 * @version 0.0.4
	 */
	initContent () {
		handle100HeightContent() // handles 100% height elements(block, portlet, etc)
		handleTabs() // handle bootstrap tabs
		handleSidebarAndContentHeight()

		addResizeHandler(handleSidebarAndContentHeight) // recalculate sidebar & content height on window resize
		addResizeHandler(handle100HeightContent) // reinitialize content height on window resize
	}
}



// WEBPACK FOOTER //
// ./src/global.js